import { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Phone, Target, TrendingUp, Calculator, CalendarIcon, BarChart3, FileText } from "lucide-react";
import api from "@/lib/api";
import { formatNumber } from "@/lib/utils";
import OfferList from "@/components/OfferList";
import OrderList from "@/components/OrderList";

function toStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function fmtDMY(ds) {
  if (!ds) return "–";
  const d = new Date(ds + "T00:00:00");
  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
}
function fmt1(v) {
  if (v == null || isNaN(v)) return "–";
  return new Intl.NumberFormat("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(v);
}
function fmt2(v) {
  if (v == null || isNaN(v)) return "0,00";
  return new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
}

function calcRange(entries, from, to) {
  const filtered = entries.filter((e) => e.date >= from && e.date <= to);
  const calls = filtered.reduce((s, e) => s + e.calls, 0);
  const orders = filtered.reduce((s, e) => s + e.orders, 0);
  const cpo = orders > 0 ? calls / orders : null;
  const rate = calls > 0 ? (orders / calls) * 100 : null;
  return { calls, orders, cpo, rate };
}

export default function VertriebPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offerStats, setOfferStats] = useState(null);
  const [offerPeriod, setOfferPeriod] = useState("365");
  const [allOrders, setAllOrders] = useState([]);

  // Gemeinsame Planungseingaben (steuern beide Rechner)
  const [planAdd, setPlanAdd] = useState("");
  const [planDate, setPlanDate] = useState("");

  // Effizienz-Vergleich
  const [compFrom, setCompFrom] = useState("");
  const [compTo, setCompTo] = useState("");

  const loadAll = useCallback(() => {
    Promise.all([
      api.get("/sales-cockpit"),
      api.get("/offer-stats", { params: { period: offerPeriod } }),
      api.get("/orders"),
    ]).then(([cockpit, stats, orders]) => {
      const r = cockpit.data;
      setData(r);
      setOfferStats(stats.data);
      setAllOrders(orders.data);
      if (r.first_date && !compFrom) {
        setCompFrom(r.first_date);
        const t = new Date();
        t.setHours(0, 0, 0, 0);
        const d90start = new Date(t);
        d90start.setDate(d90start.getDate() - 89);
        const dayBefore = new Date(d90start);
        dayBefore.setDate(dayBefore.getDate() - 1);
        const dbStr = toStr(dayBefore);
        setCompTo(dbStr >= r.first_date ? dbStr : r.first_date);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [compFrom, offerPeriod]);

  const changeOfferPeriod = (p) => {
    setOfferPeriod(p);
    api.get("/offer-stats", { params: { period: p } })
      .then((r) => setOfferStats(r.data))
      .catch(() => {});
  };

  useEffect(() => { loadAll(); }, []);

  // ── Gemeinsame Planung ──
  const planning = useMemo(() => {
    const need = Math.max(0, parseInt(planAdd, 10) || 0);
    let weeks = null, dateError = null;
    if (planDate) {
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const t = new Date(planDate + "T00:00:00");
      const diff = t - today;
      if (diff <= 0) dateError = "past";
      else weeks = Math.ceil(diff / 86400000) / 7;
    }
    return { need, weeks, dateError };
  }, [planAdd, planDate]);

  // Angebotsprognose (Annahmequote unter entschiedenen Angeboten)
  const offerForecast = useMemo(() => {
    const { need, weeks, dateError } = planning;
    if (need <= 0) return { zero: true };
    const ar = offerStats?.accept_rate;
    if (ar == null || ar <= 0) return { error: "no_rate" };
    const needed = Math.ceil(need / (ar / 100));
    const perWeek = weeks && weeks > 0 ? Math.ceil(needed / weeks) : null;
    return { needed, perWeek, dateError, acceptRate: ar, weeks };
  }, [planning, offerStats]);

  // Cold-Call-Prognose (Cold Calls pro Auftrag)
  const ccForecast = useMemo(() => {
    const { need, weeks, dateError } = planning;
    if (need <= 0) return { zero: true };
    const cpo = data?.calls_per_order;
    if (!cpo || cpo <= 0) return { error: "no_cpo" };
    const needed = Math.ceil(need * cpo);
    const perWeek = weeks && weeks > 0 ? Math.ceil(needed / weeks) : null;
    return { needed, perWeek, dateError, cpo, weeks };
  }, [planning, data]);

  const todayStr = useMemo(() => toStr(new Date()), []);
  const last90Start = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - 89);
    return toStr(d);
  }, []);

  const effTotal = useMemo(() => {
    if (!data?.entries_data) return null;
    return calcRange(data.entries_data, "2000-01-01", "2099-12-31");
  }, [data]);
  const effComp = useMemo(() => {
    if (!data?.entries_data || !compFrom || !compTo) return null;
    if (compFrom > compTo) return null;
    return calcRange(data.entries_data, compFrom, compTo);
  }, [data, compFrom, compTo]);
  const eff90 = useMemo(() => {
    if (!data?.entries_data) return null;
    return calcRange(data.entries_data, last90Start, todayStr);
  }, [data, last90Start, todayStr]);

  const compValid = compFrom && compTo && compFrom <= compTo;
  const compMinDate = data?.first_date || "";
  const compMaxDate = todayStr;

  if (loading) {
    return (
      <div className="p-4 md:p-8 space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-24 bg-zinc-900/50 animate-pulse" />
        ))}
      </div>
    );
  }
  if (!data) return <div className="p-8 text-zinc-500">Fehler beim Laden.</div>;

  const hasSalesData = data.total_cold_calls > 0 || data.total_zusagen > 0;
  const planHint = planning.dateError === "past"
    ? <p className="text-[10px] text-red-400 mt-1" data-testid="plan-date-error">Das Zieldatum muss in der Zukunft liegen.</p>
    : null;

  // Wiederverwendbarer Prognose-Block
  const ForecastBox = ({ fc, unit, basis }) => {
    if (fc.zero) {
      return <p className="font-mono text-2xl font-bold text-zinc-200" data-testid="fc-zero">0 {unit}</p>;
    }
    if (fc.error === "no_rate") {
      return <p className="text-xs text-amber-400">Noch keine entschiedenen Angebote – Annahmequote nicht berechenbar.</p>;
    }
    if (fc.error === "no_cpo") {
      return <p className="text-xs text-amber-400">Noch keine Cold-Call-/Auftragsdaten für eine Prognose.</p>;
    }
    return (
      <>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-[9px] text-zinc-400 uppercase tracking-wider mb-1">Gesamtbedarf</p>
            <p className="font-mono text-2xl font-bold text-amber-400" data-testid="fc-total">{fc.needed} {unit}</p>
          </div>
          <div>
            <p className="text-[9px] text-zinc-400 uppercase tracking-wider mb-1">Ø pro Woche</p>
            <p className="font-mono text-2xl font-bold text-sky-400" data-testid="fc-perweek">
              {fc.perWeek != null ? fc.perWeek : "–"}
            </p>
          </div>
        </div>
        {fc.perWeek == null && !fc.dateError && (
          <p className="text-[9px] text-zinc-500 mt-1">Für den Wochenbedarf ein Zieldatum wählen.</p>
        )}
        {planHint}
        <p className="text-[9px] text-zinc-500 border-t border-zinc-800/50 pt-2 mt-2">{basis}</p>
      </>
    );
  };

  return (
    <div className="p-4 md:p-8 space-y-5 max-w-4xl mx-auto" data-testid="vertrieb-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight">Vertrieb</h1>
        <p className="text-zinc-400 text-sm mt-0.5">Übersicht, Angebote & Cold Calls</p>
      </div>

      {/* ── Gemeinsamer Kopfbereich: Übersicht & Planung ── */}
      <Card className="bg-card border-l-2 border-l-amber-500/60 border-t-0 border-r border-b border-border" data-testid="overview-card">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <Target className="h-4 w-4 text-amber-500/70" />
            <p className="text-[11px] uppercase tracking-wider text-amber-400 font-medium">Übersicht & Planung</p>
          </div>

          {/* Auftragszahl + Bezugszeitraum */}
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div data-testid="kpi-orders-total">
              <p className="text-[9px] text-zinc-400 uppercase tracking-wider mb-1">Gewonnene Aufträge</p>
              <div className="flex items-baseline gap-1.5">
                <TrendingUp className="h-4 w-4 text-emerald-500 flex-shrink-0 relative top-[2px]" />
                <p className="font-mono text-3xl font-bold text-emerald-400">{data.total_zusagen}</p>
              </div>
            </div>
            <p className="text-[10px] text-zinc-400" data-testid="orders-period">
              Bezugszeitraum: gesamt{data.first_date ? ` seit ${fmtDMY(data.first_date)}` : ""}
              {" · "}{data.total_entries} Einträge
            </p>
          </div>

          {/* Auftragsübersicht (eingeklappt) */}
          <OrderList orders={allOrders} onDataChange={loadAll} />

          {/* Gemeinsame Planungseingaben */}
          <div className="mt-4 border-t border-zinc-800/60 pt-4">
            <div className="flex items-center gap-2 mb-2">
              <Calculator className="h-3.5 w-3.5 text-sky-500/70" />
              <p className="text-[10px] uppercase tracking-wider text-sky-400 font-medium">Freie Planung</p>
            </div>
            <p className="text-[10px] text-zinc-400 mb-3">
              Steuert beide Prognosen. „Zusätzliche Aufträge“ ist der noch gewünschte Zuwachs;
              bereits gewonnene Aufträge werden davon nicht abgezogen.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-zinc-400 uppercase tracking-wider block mb-1.5">Gewünschte zusätzliche Aufträge</label>
                <input type="number" min="0" step="1" value={planAdd}
                  onChange={(e) => setPlanAdd(e.target.value)} placeholder="z.B. 5"
                  className="w-full h-11 bg-zinc-950/50 border border-zinc-700 text-white font-mono text-sm px-3 focus:outline-none focus:border-zinc-500 placeholder:text-zinc-500"
                  data-testid="plan-additional" />
              </div>
              <div>
                <label className="text-[10px] text-zinc-400 uppercase tracking-wider block mb-1.5">
                  <span className="flex items-center gap-1"><CalendarIcon className="h-3 w-3" />Zieldatum</span>
                </label>
                <input type="date" value={planDate} onChange={(e) => setPlanDate(e.target.value)}
                  min={todayStr}
                  className="w-full h-11 bg-zinc-950/50 border border-zinc-700 text-white font-mono text-sm px-3 focus:outline-none focus:border-zinc-500"
                  data-testid="plan-date" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Reiter ── */}
      <Tabs defaultValue="angebote" className="w-full">
        <TabsList className="grid w-full grid-cols-2 bg-zinc-900/60 border border-zinc-800">
          <TabsTrigger value="angebote" data-testid="tab-angebote" className="data-[state=active]:bg-cyan-500/20 data-[state=active]:text-[#1f3a55] text-zinc-400 text-xs">
            Angebote
          </TabsTrigger>
          <TabsTrigger value="coldcalls" data-testid="tab-coldcalls" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-[#1f3a55] text-zinc-400 text-xs">
            Cold Calls
          </TabsTrigger>
        </TabsList>

        {/* ═══ REITER ANGEBOTE ═══ */}
        <TabsContent value="angebote" className="space-y-5 mt-4">
          {/* Angebotsstatistik + Quoten */}
          {offerStats && (
            <Card className="bg-card border-l-2 border-l-cyan-500/60 border-t-0 border-r border-b border-border" data-testid="offer-stats-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="h-4 w-4 text-cyan-500/70" />
                  <p className="text-[11px] uppercase tracking-wider text-cyan-400 font-medium">Angebotsstatistik</p>
                  <div className="ml-auto flex items-center gap-1" data-testid="offer-period-toggle">
                    <button onClick={() => changeOfferPeriod("365")}
                      className={`text-[9px] px-2 py-0.5 border transition-colors ${offerPeriod === "365" ? "border-cyan-500/50 bg-cyan-500/20 text-[#1f3a55] font-medium" : "border-zinc-700 text-zinc-400"}`}
                      data-testid="offer-period-365">Letzte 365 Tage</button>
                    <button onClick={() => changeOfferPeriod("all")}
                      className={`text-[9px] px-2 py-0.5 border transition-colors ${offerPeriod === "all" ? "border-cyan-500/50 bg-cyan-500/20 text-[#1f3a55] font-medium" : "border-zinc-700 text-zinc-400"}`}
                      data-testid="offer-period-all">Gesamt bisher</button>
                  </div>
                </div>
                <p className="text-[9px] text-zinc-400 font-mono mb-4" data-testid="offer-period-label">
                  {offerPeriod === "365"
                    ? `Zeitraum: Letzte 365 Tage (${fmtDMY(offerStats.period?.from)} – ${fmtDMY(offerStats.period?.to)})`
                    : `Zeitraum: Gesamt bisher${offerStats.period?.from ? ` (ab ${fmtDMY(offerStats.period.from)})` : ""}`}
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                  <div>
                    <p className="text-[9px] text-zinc-400 uppercase tracking-wider mb-1">Versendet</p>
                    <p className="font-mono text-lg font-bold text-white">{offerStats.sent_365}</p>
                  </div>
                  <div>
                    <p className="text-[9px] text-zinc-400 uppercase tracking-wider mb-1">Angenommen</p>
                    <p className="font-mono text-lg font-bold text-emerald-400">{offerStats.accepted_365}</p>
                  </div>
                  <div>
                    <p className="text-[9px] text-zinc-400 uppercase tracking-wider mb-1">Abgelehnt</p>
                    <p className="font-mono text-lg font-bold text-red-400">{offerStats.rejected_365}</p>
                  </div>
                  <div>
                    <p className="text-[9px] text-zinc-400 uppercase tracking-wider mb-1">Offen</p>
                    <p className="font-mono text-lg font-bold text-amber-400">{offerStats.open_offers}</p>
                  </div>
                </div>

                {/* Quote 1: Zusagequote der versendeten Angebote */}
                <div className="bg-emerald-500/10 border border-emerald-500/30 p-3 mb-3" data-testid="conversion-rate-sent">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-[10px] text-emerald-300/90 uppercase tracking-wider">Zusagequote der versendeten Angebote</p>
                      <p className="text-[9px] text-zinc-400 mt-0.5">Angenommen ÷ alle versendeten ({offerPeriod === "365" ? "365 Tage" : "Gesamt bisher"})</p>
                    </div>
                    <p className="font-mono text-3xl font-bold text-emerald-400 shrink-0" data-testid="conversion-rate-value">
                      {offerStats.sent_365 > 0
                        ? `${fmt2(offerStats.conversion_rate_sent)}%`
                        : <span className="text-base text-zinc-400 font-normal">Noch keine Daten</span>}
                    </p>
                  </div>
                  {offerStats.sent_365 > 0 && (
                    <p className="text-[9px] text-zinc-400 mt-1.5 border-t border-emerald-500/20 pt-1.5">
                      {offerStats.accepted_of_sent_365} von {offerStats.sent_365} versendeten Angeboten angenommen
                    </p>
                  )}
                </div>

                {/* Quote 2: Annahmequote unter entschiedenen Angeboten (unabhängig vom Jahresziel) */}
                <div className="bg-zinc-950/40 border border-zinc-800/50 p-3 mb-3" data-testid="accept-rate-decided">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-[10px] text-zinc-300 uppercase tracking-wider">Annahmequote unter entschiedenen Angeboten</p>
                      <p className="text-[9px] text-zinc-400 mt-0.5">Angenommen ÷ (angenommen + abgelehnt), {offerPeriod === "365" ? "365 Tage" : "Gesamt bisher"}</p>
                    </div>
                    <p className="font-mono text-2xl font-bold text-cyan-400 shrink-0" data-testid="accept-rate-value">
                      {offerStats.decided_365 > 0 && offerStats.accept_rate != null
                        ? `${fmt2(offerStats.accept_rate)}%`
                        : <span className="text-sm text-zinc-400 font-normal">Noch keine Entscheidungen</span>}
                    </p>
                  </div>
                  {offerStats.decided_365 > 0 && (
                    <p className="text-[9px] text-zinc-400 mt-1.5 border-t border-zinc-800/50 pt-1.5">
                      {offerStats.accepted_365} angenommen, {offerStats.rejected_365} abgelehnt von {offerStats.decided_365} entschiedenen
                    </p>
                  )}
                </div>

                {/* Jahresziel-Prognose (getrennt, Restbedarf zum Kalenderjahr) */}
                {offerStats.yearly_goal_orders > 0 && (
                  <div className="border-t border-zinc-800/40 pt-3" data-testid="yearly-goal-offer">
                    <p className="text-[9px] text-zinc-400 uppercase tracking-wider mb-1">
                      Jahresziel-Prognose: {offerStats.yearly_goal_orders} Aufträge
                      ({offerStats.year_orders_count} erreicht, {offerStats.rest_orders ?? 0} offen)
                    </p>
                    {offerStats.rest_orders === 0 ? (
                      <p className="text-xs text-emerald-400">Jahresziel bereits erreicht.</p>
                    ) : offerStats.offer_forecast ? (
                      <div className="space-y-0.5">
                        <p className="text-xs text-zinc-200">
                          Angebotsbedarf Jahresziel: ca. <span className="font-mono text-white">{offerStats.offer_forecast.needed}</span> Angebote
                          <span className="text-[9px] text-zinc-500"> (offen: {offerStats.offer_forecast.open_count})</span>
                        </p>
                        <p className="text-[10px] text-zinc-400" data-testid="offer-forecast-weekly">
                          Verbleibend: <span className="font-mono text-zinc-200">{fmt1(offerStats.offer_forecast.weeks_left)}</span> Wochen bis 31.12. ·
                          ca. <span className="font-mono text-zinc-200">{offerStats.offer_forecast.per_week}</span> Angebote/Woche
                        </p>
                      </div>
                    ) : offerStats.accept_rate === 0 ? (
                      <p className="text-[10px] text-zinc-400">Annahmequote 0 % – kein endlicher Bedarf berechenbar.</p>
                    ) : (
                      <p className="text-[10px] text-zinc-400">Noch nicht genug Entscheidungen für eine Prognose.</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Angebotsprognose (freie Planung) */}
          <Card className="bg-card border-l-2 border-l-sky-500/60 border-t-0 border-r border-b border-border" data-testid="offer-forecast-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Calculator className="h-4 w-4 text-sky-500/70" />
                <p className="text-[11px] uppercase tracking-wider text-sky-400 font-medium">Angebotsprognose</p>
              </div>
              <ForecastBox
                fc={offerForecast}
                unit="Angebote"
                basis={`Grundlage: Annahmequote unter entschiedenen Angeboten (${offerStats ? fmt2(offerStats.accept_rate) : "–"} %), 365-Tage-Auswertung. Orientierungswert; offene Angebote (${offerStats?.open_offers ?? 0}) sind noch nicht angerechnet.`}
              />
            </CardContent>
          </Card>

          {/* Angebotsverwaltung */}
          <OfferList orders={allOrders} onDataChange={loadAll} />
        </TabsContent>

        {/* ═══ REITER COLD CALLS ═══ */}
        <TabsContent value="coldcalls" className="space-y-5 mt-4">
          {/* Cold-Call-Kennzahlen */}
          <Card className="bg-card border-l-2 border-l-amber-500/60 border-t-0 border-r border-b border-border" data-testid="coldcall-kpis">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-4">
                <Phone className="h-4 w-4 text-amber-500/70" />
                <p className="text-[11px] uppercase tracking-wider text-amber-400 font-medium">Cold-Call-Kennzahlen</p>
              </div>
              {hasSalesData ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div data-testid="kpi-cold-calls">
                    <p className="text-[9px] text-zinc-400 uppercase tracking-wider mb-1">Cold Calls gesamt</p>
                    <p className="font-mono text-2xl font-bold text-white">{data.total_cold_calls}</p>
                  </div>
                  <div data-testid="kpi-orders">
                    <p className="text-[9px] text-zinc-400 uppercase tracking-wider mb-1">Aufträge gesamt</p>
                    <p className="font-mono text-2xl font-bold text-emerald-400">{data.total_zusagen}</p>
                  </div>
                  <div data-testid="kpi-cpo">
                    <p className="text-[9px] text-zinc-400 uppercase tracking-wider mb-1">Cold Calls pro Auftrag</p>
                    <p className="font-mono text-2xl font-bold text-white">
                      {data.calls_per_order > 0 ? formatNumber(data.calls_per_order) : "–"}
                    </p>
                  </div>
                  <div data-testid="kpi-orders-per-call">
                    <p className="text-[9px] text-zinc-400 uppercase tracking-wider mb-1">Aufträge pro Cold Call</p>
                    <p className="font-mono text-2xl font-bold text-sky-400">
                      {data.close_rate > 0 ? `${formatNumber(data.close_rate)}%` : "–"}
                    </p>
                  </div>
                </div>
              ) : (
                <p className="text-zinc-400 text-sm py-4 text-center" data-testid="no-sales-data">
                  Noch keine Vertriebsdaten erfasst. Erstelle Einträge mit Cold Calls und Zusagen.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Cold-Call-Prognose (freie Planung) */}
          <Card className="bg-card border-l-2 border-l-sky-500/60 border-t-0 border-r border-b border-border" data-testid="cc-forecast-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Calculator className="h-4 w-4 text-sky-500/70" />
                <p className="text-[11px] uppercase tracking-wider text-sky-400 font-medium">Cold-Call-Prognose</p>
              </div>
              <ForecastBox
                fc={ccForecast}
                unit="Calls"
                basis={`Grundlage: ${data.calls_per_order > 0 ? formatNumber(data.calls_per_order) : "–"} Cold Calls pro Auftrag (zeitbezogener Verhältniswert). Unabhängig von der Angebotsprognose.`}
              />
            </CardContent>
          </Card>

          {/* Jahresziel-Prognose Cold Calls (getrennt) */}
          {offerStats?.yearly_goal_orders > 0 && offerStats.cc_forecast && offerStats.rest_orders > 0 && (
            <Card className="bg-card border border-zinc-800" data-testid="yearly-goal-cc">
              <CardContent className="p-4">
                <p className="text-[9px] text-zinc-400 uppercase tracking-wider mb-1">
                  Jahresziel-Prognose ({offerStats.rest_orders} Aufträge offen)
                </p>
                <p className="text-xs text-zinc-200">
                  Cold-Call-Bedarf Jahresziel: ca. <span className="font-mono text-white">{offerStats.cc_forecast.needed}</span> Calls
                  <span className="text-[9px] text-zinc-500"> (ca. {offerStats.cc_forecast.per_week}/Woche, {offerStats.cc_forecast.weeks_left} Wochen)</span>
                </p>
              </CardContent>
            </Card>
          )}

          {/* Entwicklung der Vertriebseffizienz */}
          {data.entries_data && data.entries_data.length > 0 && (
            <Card className="bg-card border-l-2 border-l-violet-500/60 border-t-0 border-r border-b border-border" data-testid="efficiency-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-4">
                  <BarChart3 className="h-4 w-4 text-violet-500/70" />
                  <p className="text-[11px] uppercase tracking-wider text-violet-400 font-medium">Entwicklung der Vertriebseffizienz</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="bg-zinc-950/50 border border-zinc-800/40 p-3" data-testid="eff-total">
                    <p className="text-[9px] text-zinc-400 uppercase tracking-wider mb-3">Gesamt seit App-Start</p>
                    {effTotal && (
                      <div className="space-y-2">
                        <div className="flex justify-between"><span className="text-[10px] text-zinc-400">Cold Calls</span><span className="font-mono text-xs text-white">{effTotal.calls}</span></div>
                        <div className="flex justify-between"><span className="text-[10px] text-zinc-400">Aufträge</span><span className="font-mono text-xs text-emerald-400">{effTotal.orders}</span></div>
                        <div className="border-t border-zinc-800/40 pt-2 flex justify-between"><span className="text-[10px] text-zinc-400">Calls / Auftrag</span><span className="font-mono text-xs text-white">{fmt1(effTotal.cpo)}</span></div>
                        <div className="flex justify-between"><span className="text-[10px] text-zinc-400">Aufträge / Call</span><span className="font-mono text-xs text-sky-400">{effTotal.rate != null ? `${fmt2(effTotal.rate)}%` : "–"}</span></div>
                      </div>
                    )}
                  </div>

                  <div className="bg-zinc-950/50 border border-zinc-800/40 p-3" data-testid="eff-comparison">
                    <p className="text-[9px] text-zinc-400 uppercase tracking-wider mb-2">Vergleichszeitraum</p>
                    <div className="flex flex-col gap-2 mb-3">
                      <div>
                        <label className="text-[9px] text-zinc-400 uppercase tracking-wider block mb-0.5">Von</label>
                        <input type="date" value={compFrom} onChange={(e) => setCompFrom(e.target.value)}
                          min={compMinDate} max={compMaxDate}
                          className="w-full h-9 bg-zinc-950 border border-zinc-700 text-[11px] text-zinc-100 font-mono px-2 focus:outline-none focus:border-zinc-500"
                          data-testid="comp-from" />
                      </div>
                      <div>
                        <label className="text-[9px] text-zinc-400 uppercase tracking-wider block mb-0.5">Bis</label>
                        <input type="date" value={compTo} onChange={(e) => setCompTo(e.target.value)}
                          min={compMinDate} max={compMaxDate}
                          className="w-full h-9 bg-zinc-950 border border-zinc-700 text-[11px] text-zinc-100 font-mono px-2 focus:outline-none focus:border-zinc-500"
                          data-testid="comp-to" />
                      </div>
                    </div>
                    {!compValid ? (
                      <p className="text-[10px] text-red-400">Von-Datum darf nicht nach Bis-Datum liegen.</p>
                    ) : effComp && (
                      <div className="space-y-2">
                        <div className="flex justify-between"><span className="text-[10px] text-zinc-400">Cold Calls</span><span className="font-mono text-xs text-white">{effComp.calls}</span></div>
                        <div className="flex justify-between"><span className="text-[10px] text-zinc-400">Aufträge</span><span className="font-mono text-xs text-emerald-400">{effComp.orders}</span></div>
                        <div className="border-t border-zinc-800/40 pt-2 flex justify-between"><span className="text-[10px] text-zinc-400">Calls / Auftrag</span><span className="font-mono text-xs text-white">{fmt1(effComp.cpo)}</span></div>
                        <div className="flex justify-between"><span className="text-[10px] text-zinc-400">Aufträge / Call</span><span className="font-mono text-xs text-sky-400">{effComp.rate != null ? `${fmt2(effComp.rate)}%` : "–"}</span></div>
                      </div>
                    )}
                  </div>

                  <div className="bg-zinc-950/50 border border-zinc-800/40 p-3" data-testid="eff-90days">
                    <p className="text-[9px] text-zinc-400 uppercase tracking-wider mb-3">Letzte 90 Tage</p>
                    {eff90 && (
                      <div className="space-y-2">
                        <div className="flex justify-between"><span className="text-[10px] text-zinc-400">Cold Calls</span><span className="font-mono text-xs text-white">{eff90.calls}</span></div>
                        <div className="flex justify-between"><span className="text-[10px] text-zinc-400">Aufträge</span><span className="font-mono text-xs text-emerald-400">{eff90.orders}</span></div>
                        <div className="border-t border-zinc-800/40 pt-2 flex justify-between"><span className="text-[10px] text-zinc-400">Calls / Auftrag</span><span className="font-mono text-xs text-white">{fmt1(eff90.cpo)}</span></div>
                        <div className="flex justify-between"><span className="text-[10px] text-zinc-400">Aufträge / Call</span><span className="font-mono text-xs text-sky-400">{eff90.rate != null ? `${fmt2(eff90.rate)}%` : "–"}</span></div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-4 bg-zinc-950/30 border border-zinc-800/30 p-3" data-testid="eff-statement">
                  {compValid && eff90 && effComp && eff90.cpo !== null && effComp.cpo !== null ? (() => {
                    const diff = Math.round((effComp.cpo - eff90.cpo) * 10) / 10;
                    if (diff > 0) return (
                      <>
                        <p className="text-[10px] text-emerald-400">In den letzten 90 Tagen wurden weniger Cold Calls pro Auftrag benötigt als im gewählten Vergleichszeitraum.</p>
                        <p className="text-[10px] text-zinc-300 mt-1">{fmt1(Math.abs(diff))} Calls weniger pro Auftrag.</p>
                      </>
                    );
                    if (diff < 0) return (
                      <>
                        <p className="text-[10px] text-amber-400">In den letzten 90 Tagen wurden mehr Cold Calls pro Auftrag benötigt als im gewählten Vergleichszeitraum.</p>
                        <p className="text-[10px] text-zinc-300 mt-1">{fmt1(Math.abs(diff))} Calls mehr pro Auftrag.</p>
                      </>
                    );
                    return <p className="text-[10px] text-zinc-300">Die Cold Calls pro Auftrag sind gegenüber dem gewählten Vergleichszeitraum unverändert.</p>;
                  })() : (
                    <p className="text-[10px] text-zinc-400">Für diesen Vergleich liegen noch nicht genügend Daten vor.</p>
                  )}
                </div>

                <p className="text-[8px] text-zinc-500 mt-3 leading-relaxed">
                  Die Werte vergleichen Aktivitäten und Abschlüsse innerhalb derselben Zeiträume. Einzelne Cold Calls sind nicht direkt einzelnen Aufträgen zugeordnet. Die Auswertung zeigt daher eine Tendenz, keine eindeutige Ursache-Wirkungs-Zuordnung.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
